import filter from 'lodash/fp/filter'
import map from 'lodash/fp/map'
import over from 'lodash/fp/over'
import pipe from 'lodash/fp/pipe'
import * as Redux from 'redux'
import * as Elasticsearch from '../../elasticsearch'
import * as Request from '../../request'
import * as Constants from './constants'
import * as Types from './types'
import * as Utils from './utils'

const initialViewState: Types.ViewState = {
  emailActions: {isLoading: true},
  tags: {isLoading: true},
}
const viewReducer = (state = initialViewState, action: Redux.Action) => {
  switch (action.type) {
    case Request.requestConstant(Constants.FETCH_HOME_REPORT_TAGS):
      return {
        ...state,
        tags: {isLoading: true},
      }
    case Request.requestConstant(Constants.FETCH_HOME_REPORT_EMAIL_ACTIONS):
    case Request.requestConstant(Constants.FETCH_ACTIVE_MEMBERS):
      return {
        ...state,
        emailActions: {isLoading: true},
      }
    case Request.successConstant(Constants.FETCH_HOME_REPORT_TAGS):
    case Request.errorConstant(Constants.FETCH_HOME_REPORT_TAGS):
      return {
        ...state,
        tags: {isLoading: false},
      }
    case Request.successConstant(Constants.FETCH_HOME_REPORT_EMAIL_ACTIONS):
    case Request.errorConstant(Constants.FETCH_HOME_REPORT_EMAIL_ACTIONS):
    case Request.successConstant(Constants.FETCH_ACTIVE_MEMBERS):
    case Request.errorConstant(Constants.FETCH_ACTIVE_MEMBERS):
    return {
        ...state,
        emailActions: {isLoading: false},
      }
    default:
      return state
  }
}

const selectTags = Elasticsearch.createAggSelector(Elasticsearch.AggFieldNames.TopTerms)
const tagCloudReducer = (state: Types.TagState[] = [], action: Request.SuccessAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_HOME_REPORT_TAGS):
      return selectTags(action.payload.response)
        .map(({key, doc_count}: Elasticsearch.Bucket) => ({value: key, count: doc_count}))
    default:
      return state
  }
}

const selectEngagementChart = pipe(
  Elasticsearch.createAggSelector(Elasticsearch.AggFieldNames.Engagement),
  over([
    Elasticsearch.findTerm('click'),
    Elasticsearch.findTerm('open'),
    Elasticsearch.findTerm('delivered'),
  ]),
  map(Elasticsearch.selectAggBuckets(Elasticsearch.AggFieldNames.TotalCount)),
  ([click = [], open = [], delivered = []]) =>
    delivered
    .filter(({doc_count}: Elasticsearch.Bucket) => doc_count > 0)
    .map(({
      doc_count: deliveredTotalCount,
      key_as_string: timestamp,
      unique_count: {value: deliveredUniqueCount},
    }: Types.EngagementBucket) => ({
      timestamp,
      totalClickRate:
        Utils.asPercentage(Utils.selectTotalCountFromBucket({bucket: click, timestamp})
        / Utils.selectTotalCountFromBucket({bucket: delivered, timestamp})),
      totalOpenRate:
        Utils.asPercentage(Utils.selectTotalCountFromBucket({bucket: open, timestamp})
        / Utils.selectTotalCountFromBucket({bucket: delivered, timestamp})),
      uniqueClickRate:
        Utils.asPercentage(Utils.selectUniqueCountFromBucket({bucket: click, timestamp})
        / Utils.selectUniqueCountFromBucket({bucket: delivered, timestamp})),
      uniqueOpenRate:
        Utils.asPercentage(Utils.selectUniqueCountFromBucket({bucket: open, timestamp})
        / Utils.selectUniqueCountFromBucket({bucket: delivered, timestamp})),
    })),
)
const selectBigNumbers = pipe(
  Elasticsearch.createAggSelector(Elasticsearch.AggFieldNames.Engagement),
  over([
    Elasticsearch.findTerm('click'),
    Elasticsearch.findTerm('open'),
    Elasticsearch.findTerm('delivered'),
    Elasticsearch.findTerm('hard_bounce'),
    Elasticsearch.findTerm('dropped'),
    Elasticsearch.findTerm('spamreport'),
  ]),
  ([click, open, delivered, hardBounce, dropped, spamreport]) => ([
    Elasticsearch.selectDocCount(click),
    Elasticsearch.selectDocCount(open),
    Elasticsearch.selectDocCount(delivered),
    Elasticsearch.selectDocCount(hardBounce),
    Elasticsearch.selectDocCount(dropped),
    Elasticsearch.selectDocCount(spamreport),
    Utils.selectUniqueAggCount(click),
    Utils.selectUniqueAggCount(open),
    Utils.selectUniqueAggCount(delivered),
  ]),
  ([click, open, delivered, hardBounce, dropped, spamreport, uniqueClick, uniqueOpen, uniqueDelivered]) => ({
    bounceRate: Utils.asPercentage(
      (hardBounce + dropped + spamreport) / (hardBounce + dropped + spamreport + delivered)),
    totalClickRate: Utils.asPercentage(click / delivered),
    totalOpenRate: Utils.asPercentage(open / delivered),
    uniqueClickRate: Utils.asPercentage(uniqueClick / uniqueDelivered),
    uniqueOpenRate: Utils.asPercentage(uniqueOpen / uniqueDelivered),
  }),
)
const selectTopSourceClicks = pipe(
  Elasticsearch.createAggSelector(Elasticsearch.AggFieldNames.TopSourceClicks),
  filter(({key}: Elasticsearch.Bucket) => Boolean(key)),
  map(({doc_count: count, key: source}: Elasticsearch.Bucket) => ({source, count})),
)
const emptyEngagementState: Types.EngagementReportState = {
  bigNumbers: {totalOpenRate: 0, totalClickRate: 0, uniqueOpenRate: 0, uniqueClickRate: 0, bounceRate: 0},
  chart: [{totalOpenRate: 0, totalClickRate: 0, uniqueOpenRate: 0, uniqueClickRate: 0, bounceRate: 0, timestamp: ''}],
  topSources: [{source: '', count: 0}],
  totalActiveMembers: 0,
}
const engagementReportReducer =
(state: Types.EngagementReportState = emptyEngagementState, action: Request.SuccessAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_HOME_REPORT_EMAIL_ACTIONS):
      return {
        ...state,
        bigNumbers: selectBigNumbers(action.payload.response),
        chart: selectEngagementChart(action.payload.response),
        topSources: selectTopSourceClicks(action.payload.response),
      }
    case Request.successConstant(Constants.FETCH_ACTIVE_MEMBERS):
      return {
        ...state,
        totalActiveMembers: Utils.getAverageNewsLettersSent(action.payload.response),
      }
    default:
      return state
  }
}

export const reducer = Redux.combineReducers({
  engagementReport: engagementReportReducer,
  tagCloud: tagCloudReducer,
  view: viewReducer,
})



// WEBPACK FOOTER //
// ./src/pages/home/reducer.ts